import {
  BaseQuestionDto,
  BaseFillTheBlankQuestionDto,
  BaseTestQuestionDto,
  BaseTrueFalseQuestionDto,
  BaseOpenEndedQuestionDto,
  StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem,
} from "@packages/api/madlenAPI.schemas";
import { TrueFalseOption } from "./TrueFalseOption";
import { TestOptions } from "./TestOptions";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button, Text } from "@core/index";

// Roman numeral mapping
const romanNumerals = {
  I: 1,
  II: 2,
  III: 3,
  IV: 4,
  V: 5,
};

// Track whether current content has Roman numerals
let hasRomanNumeralsInContent = false;

// Function to process text and convert Roman numeral lists to markdown ordered lists
const processMarkdownText = (text: string | undefined | null) => {
  if (!text) return "";

  // Check if text contains Roman numeral lists
  const romanPattern = /^(I{1,3}|IV|V)\.\s+(.+)$/gm;
  hasRomanNumeralsInContent = romanPattern.test(text);

  if (hasRomanNumeralsInContent) {
    // Reset the regex for actual replacement
    romanPattern.lastIndex = 0;
    // Convert Roman numeral lists to standard numbered lists
    let processedText = text.replace(romanPattern, (match, roman, content) => {
      const number = romanNumerals[roman as keyof typeof romanNumerals];
      return number ? `${number}. ${content}` : match;
    });

    return processedText;
  }

  return text;
};

// Custom components for Markdown tables with borders and ordered lists
const markdownComponents = (useSpan: boolean) => ({
  table: ({ children }: any) => (
    <table className="border-collapse border border-gray-300 dark:border-gray-600 w-full my-4">
      {children}
    </table>
  ),
  thead: ({ children }: any) => (
    <thead className="bg-gray-100 dark:bg-gray-700">{children}</thead>
  ),
  tbody: ({ children }: any) => <tbody>{children}</tbody>,
  tr: ({ children }: any) => <tr>{children}</tr>,
  th: ({ children }: any) => (
    <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
      {children}
    </td>
  ),
  ol: ({ children }: any) => (
    <ol className="list-[upper-roman] list-inside space-y-2 ml-4 whitespace-normal">
      {children}
    </ol>
  ),
  ul: ({ children }: any) => (
    <ul className="list-disc list-inside space-y-2 my-4 ml-4">{children}</ul>
  ),
  li: ({ children }: any) => (
    <li className="text-gray-800 dark:text-gray-200">{children}</li>
  ),
  p: ({ children }: any) =>
    useSpan ? <span>{children}</span> : <p>{children}</p>,
});

interface QuestionViewerProps {
  index: number;
  question:
    | BaseQuestionDto
    | StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem;
}

export const QuestionViewer = ({
  index,
  question,
}: QuestionViewerProps & { index: number }) => {
  const { t } = useTranslation();
  const isTeacherView = "studentAnswer" in question;
  const [isExplanationVisible, setIsExplanationVisible] = useState(false);
  const [isAnswerVisible, setIsAnswerVisible] = useState(false);

  const renderStudentAnswer = () => {
    if (!isTeacherView) return null;
    const questionWithAnswer =
      question as StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem;
    const studentAnswer = (questionWithAnswer as any).studentAnswer;
    const questionType = questionWithAnswer.question.type;

    if (studentAnswer === null || studentAnswer === undefined) return null;

    if (questionType === "TF") return;

    const isCorrect =
      questionWithAnswer.score === questionWithAnswer.question.point;

    const renderAnswer = () => {
      if (questionType === "TEST") {
        const answer = studentAnswer as string;
        return answer.trim() === "" ? (
          <Text
            variant="span"
            size="base"
            status="secondary"
            className="italic"
          >
            assignments.student.emptyAnswer
          </Text>
        ) : (
          answer
        );
      }

      // if (questionType === "TF") {
      //   const answer = studentAnswer as boolean;
      //   return String(answer);
      // }

      if (questionType === "FB") {
        const answers = studentAnswer as string[];
        return answers.length === 0 ? (
          <Text
            variant="span"
            size="base"
            status="secondary"
            className="italic"
          >
            assignments.student.emptyAnswer
          </Text>
        ) : (
          answers.join(", ")
        );
      }

      if (questionType === "OE") {
        const answer = studentAnswer as string;
        return answer.trim() === "" ? (
          <Text
            variant="span"
            size="base"
            status="secondary"
            className="italic"
          >
            assignments.student.emptyAnswer
          </Text>
        ) : (
          answer
        );
      }

      return null;
    };

    return (
      <div
        className={`mt-4 p-4 rounded-lg border
        ${
          isCorrect
            ? "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800"
            : "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800"
        }`}
      >
        <Text className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
          {t("assignments.student.studentAnswer")}:
        </Text>
        <Text variant="p" className="whitespace-pre-line">
          {renderAnswer()}
        </Text>
      </div>
    );
  };

  const renderQuestionBody = () => {
    const baseQuestion = isTeacherView
      ? (
          question as StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem
        ).question
      : question;

    const renderImage = () => {
      const mediaUrl = isTeacherView
        ? (
            question as StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem
          ).question.mediaUrl
        : (baseQuestion as BaseQuestionDto).mediaUrl;

      if (!mediaUrl) return null;

      return (
        <div className="mt-4 w-full rounded-md overflow-hidden bg-gradient-to-r from-blue-50/30 to-indigo-50/30 dark:from-blue-900/20 dark:to-indigo-900/20 border border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-center p-4">
            <img
              src={mediaUrl}
              alt={t("assignments.generator.preview.questionImage")}
              className="max-h-60 max-w-full rounded shadow-sm"
            />
          </div>
        </div>
      );
    };

    if (baseQuestion.type === "FB") {
      const answers = (baseQuestion as BaseFillTheBlankQuestionDto).answer;
      const parts = (baseQuestion as BaseFillTheBlankQuestionDto).body.split(
        /({{BLANK}})/g,
      );

      return (
        <div className="space-y-4">
          <div className="flex items-start bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <span className="font-bold mr-2">{index}.</span>
            <div className="text-gray-800 dark:text-gray-200 font-medium">
              {parts.map((part, index) => {
                if (part === "{{BLANK}}") {
                  const answer = answers[(index - 1) / 2];
                  return (
                    <span
                      key={index}
                      className="inline-flex items-center mx-1 px-3 font-medium border-b-2 border-blue-500 shadow-xl"
                    >
                      {answer}
                    </span>
                  );
                }
                return (
                  <Markdown
                    key={index}
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents(true)}
                  >
                    {processMarkdownText(part)}
                  </Markdown>
                );
              })}
            </div>
          </div>
          {renderImage()}
        </div>
      );
    }

    if (baseQuestion.type === "TF") {
      return (
        <div className="space-y-4">
          <div className="flex items-start bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2">
              <span className="font-bold mr-2">{index}.</span>
            </div>
            <div className="text-gray-800 dark:text-gray-200 font-medium">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents(false)}
                className="whitespace-pre-line"
              >
                {processMarkdownText(
                  (baseQuestion as BaseTrueFalseQuestionDto).body,
                )}
              </Markdown>
            </div>
          </div>
          {renderImage()}
          <div className="flex gap-3">
            <TrueFalseOption
              value={true}
              question={
                question as StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem
              }
            />
            <TrueFalseOption
              value={false}
              question={
                question as StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem
              }
            />
          </div>
        </div>
      );
    }

    if (baseQuestion.type === "TEST") {
      return (
        <div className="space-y-4">
          <div className="flex items-start bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2">
              <span className="font-bold mr-2">{index}.</span>
            </div>
            <div className="text-gray-800 dark:text-gray-200 font-medium">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents(false)}
                className="whitespace-pre-line"
              >
                {processMarkdownText(
                  (baseQuestion as BaseTestQuestionDto).body,
                )}
              </Markdown>
            </div>
          </div>
          {renderImage()}
          <TestOptions
            question={
              question as StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem
            }
          />
        </div>
      );
    }

    if (baseQuestion.type === "OE") {
      return (
        <div className="space-y-4">
          <div className="flex items-start bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2">
              <span className="font-bold mr-2">{index}.</span>
            </div>
            <div className="text-gray-800 dark:text-gray-200 font-medium">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents(false)}
                className="whitespace-pre-line"
              >
                {processMarkdownText(
                  (baseQuestion as BaseOpenEndedQuestionDto).body,
                )}
              </Markdown>
            </div>
          </div>
          {renderImage()}
          <div className="mt-4">
            <Button
              data-testid="answer-button"
              onClick={() => setIsAnswerVisible(!isAnswerVisible)}
              size="lg"
              rounded="lg"
              bgColor="bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
              fullWidth
              variant="outline"
              className="border-blue-200 dark:border-blue-800 flex items-center justify-between"
            >
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                {t("assignments.generator.preview.correctAnswer")}
              </h4>
              {isAnswerVisible ? (
                <ChevronUp className="w-4 h-4 text-blue-800 dark:text-blue-200" />
              ) : (
                <ChevronDown className="w-4 h-4 text-blue-800 dark:text-blue-200" />
              )}
            </Button>
            {isAnswerVisible && (
              <div className="mt-2 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800 animate-fadeIn">
                <Markdown
                  className="text-gray-800 dark:text-gray-200"
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents(false)}
                >
                  {processMarkdownText(
                    (baseQuestion as BaseOpenEndedQuestionDto).answer,
                  )}
                </Markdown>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
          <div className="text-gray-800 dark:text-gray-200 font-medium">
            <div className="question-content">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents(false)}
              >
                {processMarkdownText((baseQuestion as BaseQuestionDto).body)}
              </Markdown>
            </div>
          </div>
        </div>
        {renderImage()}
      </div>
    );
  };

  const renderExplanation = () => {
    const explanation = isTeacherView
      ? (
          question as StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem
        ).question.explanation
      : (question as BaseQuestionDto).explanation;

    if (!explanation) return null;

    return (
      <div className="mt-4">
        <Button
          onClick={() => setIsExplanationVisible(!isExplanationVisible)}
          data-testid="explanation-button"
          size="lg"
          rounded="lg"
          bgColor="bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30"
          fullWidth
          variant="outline"
          className="border-yellow-200 dark:border-yellow-800 flex items-center justify-between"
        >
          <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            {question.type === "OE"
              ? t("assignments.generator.preview.criteria")
              : t("assignments.generator.preview.explanation")}
          </h4>
          {isExplanationVisible ? (
            <ChevronUp className="w-4 h-4 text-yellow-800 dark:text-yellow-200" />
          ) : (
            <ChevronDown className="w-4 h-4 text-yellow-800 dark:text-yellow-200" />
          )}
        </Button>
        {isExplanationVisible && (
          <div className="mt-2 p-4 bg-yellow-50/50 dark:bg-yellow-900/10 rounded-lg border border-yellow-200 dark:border-yellow-800 animate-fadeIn">
            <Markdown
              className="text-gray-800 dark:text-gray-200"
              remarkPlugins={[remarkGfm]}
              components={markdownComponents(false)}
            >
              {processMarkdownText(explanation?.replace(/\\n/g, "\n"))}
            </Markdown>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {renderQuestionBody()}
      {renderStudentAnswer()}
      {renderExplanation()}
    </div>
  );
};
